import { Box, createTheme, IconButton, MenuItem, ThemeProvider } from '@mui/material'
import { StyledEngineProvider } from '@mui/material/styles'

import { useUser } from 'api/users/users.api'
import {
  getAggregatedDatasets,
  normalizeDatasetId,
  serializeTableAccessor,
} from 'helpers/dataset.helper/dataset.helper'
import { tsvClipboardPayloadSerializer } from 'helpers/tableClipboard.helper/tableClipboard.helper'
import i18n from 'i18next'
import { getMRTUserLocalization } from 'localization/localization.helper'
import {
  MaterialReactTable,
  MRT_ColumnDef,
  MRT_DensityState,
  MRT_PaginationState,
  MRT_ToggleDensePaddingButton,
  MRT_ToggleGlobalFilterButton,
} from 'material-react-table'
import React, { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import alertStore from 'store/alert/alert'
import uiConfigStore from 'store/uiConfig/uiConfig'
import { dynamicClassName } from 'styles/helper'
import theme from 'styles/theme/theme'
import { colors as variablesColors } from 'styles/variables'
import Icon from 'ui/atoms/Icon/Icon'
import Datetime from 'utils/datetime/datetime'
import { addLocaleSeparators } from 'utils/numberUtils/numberUtils'
import { useSnapshot } from 'valtio'
import optimizeViewStore from 'views/OptimizeView/store/optimizeViewStore'
import ExportDatasetsMenu from '../ExportDatasetsMenu/ExportDatasetsMenu'
import { useRafThrottledDragSelection } from './customHooks/useRafThrottledDragSelection'
import {
  getTimeSeriesClipboardCellId,
  getTimeSeriesRectangularClipboardSelection,
  type TimeSeriesTableClipboardCellPosition,
  timeSeriesTableClipboardSelectionSourceAdapter,
} from './TimeSeriesDataTable.clipboard.adapter'
import {
  buildTimeSeriesSelectionVisualState,
  cropDataToPagination,
  customStringToISODateTime,
  resolveTimeSeriesCellBackgroundColor,
} from './TimeSeriesDataTable.helper'
import styles from './TimeSeriesDataTable.module.less'
import {
  TablePropsFactoryFnProps,
  TimeSeriesDataTableColumnDef,
  TimeSeriesDataTableData,
  TimeSeriesDataTableProps,
} from './types'
import { initCurrentDataPointIndex } from './utils/initCurrentDataPointIndex'

function getColumnsWithSortingEnabled(
  columns: TimeSeriesDataTableColumnDef[],
  setDataState: React.Dispatch<React.SetStateAction<TimeSeriesDataTableData[]>>,
  setCurrentDataPointIndex: React.Dispatch<React.SetStateAction<number>>,
  currentOptimizationHour: ISODateTime
): TimeSeriesDataTableColumnDef[] {
  const sortData = (accessorKey: any, ascending: boolean): void => {
    setDataState((prevDataState: TimeSeriesDataTableData[]) => {
      const sortedData = [...prevDataState].sort((aColumn, bColumn) => {
        const a = aColumn[accessorKey]
        const b = bColumn[accessorKey]

        if (a === '-' || b === '-') return a === '-' ? 1 : -1 // Sort '-' to the bottom

        if (accessorKey === 'time') {
          const dateA = new Date(a as any).getTime()
          const dateB = new Date(b as any).getTime()
          return ascending ? dateA - dateB : dateB - dateA
        }
        if (typeof a === 'string' && typeof b === 'string') return ascending ? a.localeCompare(b) : b.localeCompare(a)
        if (typeof a === 'number' && typeof b === 'number') return ascending ? a - b : b - a
        // If a and b are not of the same type, sort by type
        if (typeof a === 'number' && typeof b === 'string') return ascending ? -1 : 1
        if (typeof a === 'string' && typeof b === 'number') return ascending ? 1 : -1
        return 0
      })
      const currentDataPoint = Datetime.toLocalTime(currentOptimizationHour, 'longDayTextWithoutYear')
      const currentDataPointIndex = sortedData.findIndex((row) => row.time === currentDataPoint)

      if (currentDataPointIndex === -1) {
        setCurrentDataPointIndex(0)
      } else {
        setCurrentDataPointIndex(currentDataPointIndex)
      }

      return sortedData
    })
  }

  columns.forEach((column: TimeSeriesDataTableColumnDef) => {
    column.renderColumnActionsMenuItems = ({ closeMenu }) => [
      <MenuItem
        key={`sort-ascending-${column.accessorKey}`}
        onClick={() => {
          sortData(column.accessorKey as string, true)
          closeMenu()
        }}
      >
        <i
          className={dynamicClassName({
            ['fas fa-arrow-up' + ` ` + styles.Button__icon]: true,
            [styles.TimeSeriesDataTable_ActionsMenuItem]: true,
          })}
        />
        {i18n.t('Sort in ascending order')}
      </MenuItem>,
      <MenuItem
        key={`sort-descending-${column.accessorKey}`}
        onClick={() => {
          sortData(column.accessorKey as string, false)
          closeMenu()
        }}
      >
        <i
          className={dynamicClassName({
            ['fas fa-arrow-down' + ` ` + styles.Button__icon]: true,
            [styles.TimeSeriesDataTable_ActionsMenuItem]: true,
          })}
        />
        {i18n.t('Sort in descending order')}
      </MenuItem>,
    ]
  })

  return columns
}

function setFooterByPage(
  dataOnPage: TimeSeriesDataTableData[],
  returnIdToDecimals: Record<string, number>,
  columns: TimeSeriesDataTableColumnDef[],
  hideSumAndAverageRow?: boolean,
  hideMinMaxRow?: boolean
): void {
  const groupedData = dataOnPage.reduce((group: Record<string, (string | number)[]>, item) => {
    Object.entries(item).forEach(([key, value]) => {
      if (!group[key]) {
        group[key] = []
        group[key].push(key)
      }
      group[key].push(value as number)
    })
    return group
  }, {})

  const formattedData: { return_id: string; values: (number | null)[]; decimal?: number }[] = Object.entries(
    groupedData
  ).map(([return_id, values]) => ({
    return_id,
    values: values.slice(1) as (number | null)[],
    decimal: returnIdToDecimals[normalizeDatasetId(return_id)],
  }))

  const sumAndAverageRows: any[] = !hideSumAndAverageRow
    ? [...getAggregatedDatasets(formattedData, 'Sum'), ...getAggregatedDatasets(formattedData, 'Average')]
    : []

  const minAndMaxRows: any[] = !hideMinMaxRow
    ? [...getAggregatedDatasets(formattedData, 'Max'), ...getAggregatedDatasets(formattedData, 'Min')]
    : []

  columns.slice(1).forEach((column) => {
    setColumnFooter(column, sumAndAverageRows, minAndMaxRows, hideSumAndAverageRow, hideMinMaxRow)
  })
}

function setColumnFooter(
  column: TimeSeriesDataTableColumnDef,
  sumAndAverageRows: TimeSeriesDataTableData[],
  minAndMaxRows: TimeSeriesDataTableData[],
  hideSumAndAverageRow?: boolean,
  hideMinMaxRow?: boolean
): void {
  column.Footer = () => {
    const key = serializeTableAccessor(column.accessorKey as string)
    return (
      <>
        {!hideSumAndAverageRow && (
          <>
            <span>
              {sumAndAverageRows[0]?.[key] != null ? addLocaleSeparators(String(sumAndAverageRows[0][key])) : ''}
            </span>
            <br />
            <span>
              {sumAndAverageRows[1]?.[key] != null ? addLocaleSeparators(String(sumAndAverageRows[1][key])) : ''}
            </span>
          </>
        )}
        {!hideMinMaxRow && (
          <>
            {!hideSumAndAverageRow && <br />}
            <span>{minAndMaxRows[0]?.[key] != null ? addLocaleSeparators(String(minAndMaxRows[0][key])) : ''}</span>
            <br />
            <span>{minAndMaxRows[1]?.[key] != null ? addLocaleSeparators(String(minAndMaxRows[1][key])) : ''}</span>
          </>
        )}
      </>
    )
  }
}

export default function TimeSeriesDataTable({
  columns,
  data,
  tableKey,
  initialPageSize = 24,
  initialPageIndex,
  hideDownloadButton = false,
  datasetsInExport = [],
  datasetNames = {},
  hideTopToolbar = false,
  showInitialPageWithCurrentOptimizationHour,
  showInitialPageWithCurrentDay,
  editDisplayMode = 'modal',
  enableEditing = false,
  disableCustomSorting = false,
  tableContainerRef,
  useColumnColorFromItem,
  footerCalculatesPerPage,
  hideSumAndAverageRow,
  hideMinMaxRow,
  returnIdToDecimals,
  onMouseMove,
  max_height,
  sticky_table_header,
  onPaginationChange,
  enableExpanding = false,
  onFullScreenClick,
  showCloseButton = false,
  onCloseClick,
  containerHeight,
  enableCellRangeSelectionForClipboard = false,
}: TimeSeriesDataTableProps): ReactElement {
  const optimizeViewSnap = useSnapshot(optimizeViewStore)
  const uiConfigSnap = useSnapshot(uiConfigStore)
  const { data: user } = useUser()
  const userLocalization = getMRTUserLocalization(user?.settings.language)
  const { t } = useTranslation()

  const [globalFilter, setGlobalFilter] = useState('')
  const [density, setDensity] = useState('compact')
  const [fontScale, setFontScale] = useState('120%')
  const [fontBoldness, setFontBoldness] = useState('600')
  const localTableContainerRef = useRef<HTMLDivElement | null>(null)
  const effectiveTableContainerRef = tableContainerRef ?? localTableContainerRef
  const [lastTableKey, setLastTableKey] = useState<string>()
  const [dataState, setDataState] = useState(data)
  const [currentDataPointIndex, setCurrentDataPointIndex] = useState(
    initCurrentDataPointIndex(data, false, showInitialPageWithCurrentDay, optimizeViewSnap.currentOptimizationHour)
  ) //which hour should decide what data you see on first render?

  const tableColumns: TimeSeriesDataTableColumnDef[] = useMemo(
    () =>
      disableCustomSorting
        ? columns
        : getColumnsWithSortingEnabled(
            columns,
            setDataState,
            setCurrentDataPointIndex,
            optimizeViewSnap.currentOptimizationHour
          ),
    [columns, disableCustomSorting, optimizeViewSnap.currentOptimizationHour]
  )

  const columnOrder = useMemo(() => {
    const baseOrder = tableColumns.map((c) => c.accessorKey)
    return enableExpanding ? ['mrt-row-expand', ...baseOrder] : baseOrder
  }, [tableColumns, enableExpanding])

  // Disable cell range selection when expanding rows are enabled to avoid row index collision.
  // With subRows, row.index is only unique within parent, so parent and child rows can have
  // the same index (e.g. both 0/1), breaking clipboard selection coordinate tracking.
  // This is a safety guard until selection uses stable row identifiers (row.id or absolute index).
  const effectiveEnableCellRangeSelection = enableCellRangeSelectionForClipboard && !enableExpanding

  // Use initialPageIndex if provided, otherwise calculate from currentDataPointIndex
  const computedInitialPageIndex =
    initialPageIndex !== undefined
      ? initialPageIndex
      : Math.max(0, Math.floor(currentDataPointIndex === 0 ? 0 : currentDataPointIndex / initialPageSize))

  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: computedInitialPageIndex,
    pageSize: initialPageSize,
  })

  const [screenHeight, setScreenHeight] = useState(window.innerHeight)

  const handleWindowResize = useCallback((): void => {
    setScreenHeight(window.innerHeight)
  }, [])

  useEffect(() => {
    if (sticky_table_header) {
      window.addEventListener('resize', handleWindowResize)
      return () => window.removeEventListener('resize', handleWindowResize)
    }
  }, [sticky_table_header, handleWindowResize])

  const dataOnPage = useMemo(() => {
    return cropDataToPagination(dataState, pagination)
  }, [dataState, pagination])

  const [key, setKey] = useState(Math.random())

  useEffect(() => {
    //Quickfix to make footer calculate per page work
    if (footerCalculatesPerPage) {
      setFooterByPage(dataOnPage, returnIdToDecimals, tableColumns, hideSumAndAverageRow, hideMinMaxRow)
      setKey(Math.random()) //Force rerender to update footer
    }
  }, [tableColumns, dataOnPage, hideMinMaxRow, hideSumAndAverageRow, footerCalculatesPerPage, returnIdToDecimals])

  const reloadTable = useCallback(() => {
    setLoading(true)

    // "isLoading true => false" forces the table to rerender.
    // This is used when data is pasted into the table from excel for example.
    setTimeout(() => {
      setLoading(false)
    }, 100)
  }, [])

  useEffect(() => {
    if (tableKey !== lastTableKey) {
      setLastTableKey(tableKey)
      reloadTable()
    }
  }, [lastTableKey, reloadTable, tableKey])

  useEffect(() => {
    if (enableEditing) {
      setDataState(data)
      return // Don't add dummy data or modify pagination when editing
    }

    const originalDataPointIndex = initCurrentDataPointIndex(
      data,
      showInitialPageWithCurrentOptimizationHour,
      showInitialPageWithCurrentDay,
      optimizeViewSnap.currentOptimizationHour
    )
    const currentDataPointIndexOnPage =
      originalDataPointIndex === -1 ? -1 : originalDataPointIndex % pagination.pageSize

    // A currentDataPointIndexOnPage of 0 means that the data point we're
    // looking for is already the first row on the page (no need to add padding)
    const nrOfEmptyRows =
      originalDataPointIndex === -1 || currentDataPointIndexOnPage === 0
        ? 0
        : pagination.pageSize - currentDataPointIndexOnPage
    let currentDataPointPageNumber =
      originalDataPointIndex === -1 ? 0 : Math.floor(originalDataPointIndex / pagination.pageSize)

    if (nrOfEmptyRows > 0) {
      currentDataPointPageNumber += 1
    }

    // Only reset pagination if initialPageIndex is not provided
    // When initialPageIndex is provided, we want to preserve the user's page selection
    if (initialPageIndex === undefined) {
      setPagination({ pageIndex: currentDataPointPageNumber, pageSize: pagination.pageSize })
    }

    // Fill first rows with dummy data so that current hour is on line 0 on any page
    const dummyData = data[0] ? Object.fromEntries(Object.keys(data[0]).map((key) => [key, '-'])) : {}
    const dataWithPadding: TimeSeriesDataTableData[] = [...new Array(nrOfEmptyRows).fill(dummyData), ...data]

    const filteredData = dataWithPadding.filter((row) => {
      for (const key in row) {
        if (!globalFilter) {
          return true
        }
        if (row[key]?.toString().toLowerCase().includes(globalFilter.toLowerCase())) {
          return true
        }
      }
      return false
    })

    setDataState(filteredData)
    setCurrentDataPointIndex(
      initCurrentDataPointIndex(
        filteredData,
        showInitialPageWithCurrentOptimizationHour,
        showInitialPageWithCurrentDay,
        optimizeViewSnap.currentOptimizationHour
      )
    )
  }, [
    data,
    enableEditing,
    globalFilter,
    initialPageIndex,
    optimizeViewSnap.currentOptimizationHour,
    pagination.pageSize,
    showInitialPageWithCurrentDay,
    showInitialPageWithCurrentOptimizationHour,
  ])

  useEffect(() => {
    if (density === 'compact') {
      setFontScale('120%')
      setFontBoldness('600')
    } else if (density === 'comfortable') {
      setFontScale('130%')
      setFontBoldness('700')
    } else if (density === 'spacious') {
      setFontScale('140%')
      setFontBoldness('700')
    }
  }, [density])

  const tableTheme = useMemo(
    () =>
      createTheme({
        palette: {
          primary: theme.palette.primary,
          secondary: theme.palette.secondary,
          info: theme.palette.info,
        },
      }),
    []
  )

  const [isLoading, setLoading] = useState(false)
  const [selectedCells, setSelectedCells] = useState<Set<string>>(new Set())
  const selectionAnchorRef = useRef<TimeSeriesTableClipboardCellPosition | null>(null)
  const isDraggingSelectionRef = useRef(false)

  const { queueDragSelectionUpdate, clearPendingDragSelection, getSelectionForCopy } = useRafThrottledDragSelection({
    selectionAnchorRef,
    setSelectedCells,
  })

  const clearClipboardSelection = useCallback((): void => {
    clearPendingDragSelection()
    selectionAnchorRef.current = null
    isDraggingSelectionRef.current = false
    setSelectedCells(new Set())
  }, [clearPendingDragSelection])

  const dataOnPageWithRowData = useMemo(
    () =>
      dataOnPage.map((row) => {
        const rowTimeToISoDateTime = customStringToISODateTime(row.time)
        const rowIsAfterCurrentOptHour = Datetime.isAfter(
          Datetime.toISOString(rowTimeToISoDateTime),
          optimizeViewSnap.currentOptimizationHour
        )
        const rowIsCurrentOptHour =
          row.time === Datetime.toLocalTime(optimizeViewSnap.currentOptimizationHour, 'longDayTextWithoutYear')

        return {
          ...row,
          rowIsAfterCurrentOptHour,
          rowIsCurrentOptHour,
        }
      }),
    [dataOnPage, optimizeViewSnap.currentOptimizationHour]
  )

  const rowOrderSignature = useMemo(
    () => dataOnPageWithRowData.map((row) => String(row.time)).join('|'),
    [dataOnPageWithRowData]
  )
  const previousRowOrderSignatureRef = useRef<string | null>(null)

  useEffect(() => {
    if (!effectiveEnableCellRangeSelection) {
      previousRowOrderSignatureRef.current = rowOrderSignature
      return
    }

    if (
      previousRowOrderSignatureRef.current !== null &&
      previousRowOrderSignatureRef.current !== rowOrderSignature &&
      selectedCells.size > 0
    ) {
      clearClipboardSelection()
    }

    previousRowOrderSignatureRef.current = rowOrderSignature
  }, [clearClipboardSelection, effectiveEnableCellRangeSelection, rowOrderSignature, selectedCells.size])

  const onMrtPaginationChange = useCallback(
    (updaterOrValue: any) => {
      const newPagination = typeof updaterOrValue === 'function' ? updaterOrValue(pagination) : updaterOrValue
      if (effectiveEnableCellRangeSelection) {
        clearClipboardSelection()
      }
      setPagination(newPagination)
      if (enableEditing) {
        reloadTable()
      }

      if (onPaginationChange) {
        onPaginationChange(newPagination)
      }
    },
    [
      effectiveEnableCellRangeSelection,
      clearClipboardSelection,
      enableEditing,
      onPaginationChange,
      reloadTable,
      pagination,
    ]
  )

  const onDensityChange = useCallback(
    (updater: any) => {
      const newDensity = typeof updater === 'function' ? updater(density as MRT_DensityState) : updater
      setDensity(newDensity)
    },
    [density]
  )

  const getSubRows = useCallback((row: TimeSeriesDataTableData) => row.subRows, [])

  const filteredColumnOrder = useMemo(() => columnOrder?.filter((c) => c != null) as string[], [columnOrder])

  const selectableColumnIds = useMemo(
    () => filteredColumnOrder.filter((columnId) => columnId !== 'mrt-row-expand'),
    [filteredColumnOrder]
  )

  const selectableColumnIdToIndex = useMemo(
    () =>
      selectableColumnIds.reduce(
        (acc, columnId, index) => {
          acc[columnId] = index
          return acc
        },
        {} as Record<string, number>
      ),
    [selectableColumnIds]
  )

  const handleClipboardKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>): void => {
      if (!effectiveEnableCellRangeSelection) {
        return
      }

      if (event.key === 'Escape') {
        clearClipboardSelection()
        return
      }

      const isCopy = (event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'c'
      if (!isCopy) {
        return
      }

      const selectionForCopy = getSelectionForCopy(selectedCells)

      if (selectionForCopy.size === 0) {
        return
      }

      const payload = timeSeriesTableClipboardSelectionSourceAdapter.toClipboardPayload({
        selectedCells: selectionForCopy,
        rows: dataOnPageWithRowData,
        selectableColumnIds,
      })

      if (payload.length === 0) {
        return
      }

      navigator.clipboard.writeText(tsvClipboardPayloadSerializer.fromClipboardPayload(payload)).catch(() => {
        alertStore.error(t('Failed to copy to clipboard'))
      })
      event.preventDefault()
      event.stopPropagation()
    },
    [
      clearClipboardSelection,
      effectiveEnableCellRangeSelection,
      getSelectionForCopy,
      selectedCells,
      dataOnPageWithRowData,
      selectableColumnIds,
      t,
    ]
  )

  const handleSelectionMouseUp = useCallback((): void => {
    isDraggingSelectionRef.current = false
  }, [])

  const handleClipboardSelectionOutsideMouseDown = useCallback(
    (event: MouseEvent): void => {
      if (!effectiveEnableCellRangeSelection || !effectiveTableContainerRef.current) {
        return
      }

      const isClickInsideTable = effectiveTableContainerRef.current.contains(event.target as Node)
      if (!isClickInsideTable) {
        clearClipboardSelection()
      }
    },
    [effectiveEnableCellRangeSelection, effectiveTableContainerRef, clearClipboardSelection]
  )

  const clipboardSelectionContextKey = useMemo(
    () => `${pagination.pageIndex}:${pagination.pageSize}:${globalFilter}`,
    [globalFilter, pagination.pageIndex, pagination.pageSize]
  )
  const previousClipboardSelectionContextKeyRef = useRef<string | null>(null)

  useEffect(() => {
    if (!effectiveEnableCellRangeSelection) {
      previousClipboardSelectionContextKeyRef.current = clipboardSelectionContextKey
      return
    }

    if (
      previousClipboardSelectionContextKeyRef.current !== null &&
      previousClipboardSelectionContextKeyRef.current !== clipboardSelectionContextKey
    ) {
      clearClipboardSelection()
    }

    previousClipboardSelectionContextKeyRef.current = clipboardSelectionContextKey
  }, [clearClipboardSelection, clipboardSelectionContextKey, effectiveEnableCellRangeSelection])

  const tableState = useMemo(
    () => ({
      pagination,
      globalFilter,
      columnOrder: filteredColumnOrder,
      density: density as MRT_DensityState,
      isLoading,
    }),
    [pagination, globalFilter, filteredColumnOrder, density, isLoading]
  )

  const initialState = useMemo(
    () => ({
      columnPinning: { left: enableExpanding ? ['mrt-row-expand', 'time'] : ['time'] },
      density: density as MRT_DensityState,
      pagination: {
        pageSize: pagination.pageSize,
        pageIndex: pagination.pageIndex,
      },
      columnSizing: enableExpanding
        ? {
            'mrt-row-expand': 0,
          }
        : ({} as Record<string, number>),
    }),
    [enableExpanding, density, pagination.pageSize, pagination.pageIndex]
  )

  const muiPaginationProps = useMemo(
    () => ({
      rowsPerPageOptions: [6, 12, 24, 48, 72, 96],
    }),
    []
  )

  const muiTableContainerProps = useMemo(
    () => ({
      sx: {
        maxHeight:
          typeof max_height === 'string' || typeof max_height === 'number'
            ? max_height
            : containerHeight
              ? containerHeight - 110
              : sticky_table_header
                ? screenHeight - 250
                : 'inherit',
      },
      ref: effectiveTableContainerRef,
      onMouseMove: onMouseMove,
      tabIndex: effectiveEnableCellRangeSelection ? 0 : undefined,
      onKeyDown: handleClipboardKeyDown,
    }),
    [
      max_height,
      containerHeight,
      sticky_table_header,
      screenHeight,
      effectiveTableContainerRef,
      onMouseMove,
      effectiveEnableCellRangeSelection,
      handleClipboardKeyDown,
    ]
  )

  const muiTableBodyCellProps = useCallback(
    ({ column, row, table }: TablePropsFactoryFnProps) => {
      const allColumns = table.getAllColumns()
      const thisColumnIndex = allColumns.findIndex((c) => c.id === column.id)
      const { rowIsAfterCurrentOptHour, rowIsCurrentOptHour } = row.original
      const typedColumnDef = column.columnDef as TimeSeriesDataTableColumnDef
      const columnColor = resolveTimeSeriesCellBackgroundColor({
        rowIsCurrentOptHour: Boolean(rowIsCurrentOptHour),
        rowIsAfterCurrentOptHour: Boolean(rowIsAfterCurrentOptHour),
        markHoursAhead: uiConfigSnap.markHoursAhead,
        useColumnColorFromItem,
        thisColumnIndex,
        columnDef: typedColumnDef,
      })

      if (column.id === 'mrt-row-expand') {
        return {
          style: {
            width: '10px',
            paddingRight: '10px',
            textAlign: 'left' as const,
          },
          sx: {
            backgroundColor: columnColor,
            'tr:hover &': {
              backgroundColor: 'rgba(0, 0, 0, 0.04) !important',
            },
          },
        }
      }

      const selectionRowIndex = row.index
      const { selectionColumnIndex, isSelected, boxShadowValue } = buildTimeSeriesSelectionVisualState({
        columnId: column.id,
        selectableColumnIdToIndex,
        selectionRowIndex,
        selectedCells,
      })

      const handleCellMouseDown = (event: React.MouseEvent): void => {
        if (!effectiveEnableCellRangeSelection || selectionColumnIndex === undefined) {
          return
        }

        const currentCell = {
          rowIndex: selectionRowIndex,
          columnIndex: selectionColumnIndex,
        }

        isDraggingSelectionRef.current = true

        if (event.shiftKey && selectionAnchorRef.current) {
          setSelectedCells(getTimeSeriesRectangularClipboardSelection(selectionAnchorRef.current, currentCell))
        } else {
          selectionAnchorRef.current = currentCell
          setSelectedCells(new Set([getTimeSeriesClipboardCellId(currentCell.rowIndex, currentCell.columnIndex)]))
        }

        const containerElement = (event.currentTarget as HTMLElement).closest(
          '.MuiTableContainer-root'
        ) as HTMLElement | null
        containerElement?.focus()
        event.preventDefault()
      }

      const handleCellMouseEnter = (): void => {
        if (
          !effectiveEnableCellRangeSelection ||
          !isDraggingSelectionRef.current ||
          !selectionAnchorRef.current ||
          selectionColumnIndex === undefined
        ) {
          return
        }

        queueDragSelectionUpdate({
          rowIndex: selectionRowIndex,
          columnIndex: selectionColumnIndex,
        })
      }

      return {
        align: typedColumnDef.align,
        onMouseDown: handleCellMouseDown,
        onMouseEnter: handleCellMouseEnter,
        sx: {
          boxShadow: boxShadowValue,
          fontSize: fontScale,
          fontWeight: fontBoldness,
          fontFamily: 'monospace, monospace',
          borderRight: typedColumnDef.borderRight ? `2px solid ${variablesColors.black}` : 'none',
          backgroundColor: isSelected ? 'rgba(24, 144, 255, 0.18)' : columnColor,
          height: '2.75rem',
        },
      }
    },
    [
      effectiveEnableCellRangeSelection,
      fontBoldness,
      fontScale,
      queueDragSelectionUpdate,
      selectedCells,
      selectableColumnIdToIndex,
      uiConfigSnap.markHoursAhead,
      useColumnColorFromItem,
    ]
  )

  useEffect(() => {
    if (!effectiveEnableCellRangeSelection) {
      return
    }

    window.addEventListener('mouseup', handleSelectionMouseUp)
    return () => {
      window.removeEventListener('mouseup', handleSelectionMouseUp)
    }
  }, [effectiveEnableCellRangeSelection, handleSelectionMouseUp])

  useEffect(() => {
    document.addEventListener('mousedown', handleClipboardSelectionOutsideMouseDown)
    return () => {
      document.removeEventListener('mousedown', handleClipboardSelectionOutsideMouseDown)
    }
  }, [handleClipboardSelectionOutsideMouseDown])

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={tableTheme}>
        <MaterialReactTable
          key={key}
          enableBottomToolbar
          enableTopToolbar={!hideTopToolbar}
          enableStickyHeader={max_height || sticky_table_header ? true : undefined}
          enableStickyFooter={sticky_table_header ? true : undefined}
          editDisplayMode={editDisplayMode}
          enableEditing={enableEditing}
          enableExpanding={enableExpanding}
          getSubRows={enableExpanding ? getSubRows : undefined}
          layoutMode="grid"
          muiExpandButtonProps={({ row }) => ({
            title: row.getIsExpanded() ? t('Collapse quarters') : t('Expand quarters'),
            'aria-label': row.getIsExpanded() ? t('Collapse quarters') : t('Expand quarters'),
            style: { visibility: row.depth > 0 ? 'hidden' : 'visible' },
            children: (
              <i
                className={`fas ${row.getIsExpanded() ? 'fa-chevron-down' : 'fa-chevron-right'}`}
                style={{ fontSize: '16px' }}
              />
            ),
          })}
          muiExpandAllButtonProps={({ table }) => ({
            title: table.getIsAllRowsExpanded() ? t('Collapse all quarters') : t('Expand all quarters'),
            'aria-label': table.getIsAllRowsExpanded() ? t('Collapse all quarters') : t('Expand all quarters'),
            children: (
              <i
                className={`fas ${table.getIsAllRowsExpanded() ? 'fa-grip-lines-vertical' : 'fa-grip-lines'}`}
                style={{ fontSize: '16px' }}
              />
            ),
          })}
          columns={tableColumns as MRT_ColumnDef<TimeSeriesDataTableData>[]}
          data={dataOnPageWithRowData}
          localization={userLocalization}
          enableColumnFilters={false}
          enableColumnResizing
          enableSorting={false}
          onPaginationChange={onMrtPaginationChange}
          muiPaginationProps={muiPaginationProps}
          onGlobalFilterChange={setGlobalFilter}
          manualFiltering={true}
          manualPagination={true}
          rowCount={dataState.length}
          onDensityChange={onDensityChange}
          state={tableState}
          initialState={initialState}
          renderToolbarInternalActions={({ table }) => (
            <Box>
              <MRT_ToggleDensePaddingButton table={table} color="primary" style={{ padding: '5px' }} />
              {!showCloseButton && onFullScreenClick && (
                <IconButton
                  color="primary"
                  style={{ padding: '5px' }}
                  onClick={onFullScreenClick}
                  title={t('Toggle full screen')}
                  aria-label={t('Toggle full screen')}
                >
                  <Icon icon="fas fa-expand" size="medium" />
                </IconButton>
              )}
              {showCloseButton && (
                <IconButton
                  color="primary"
                  style={{ padding: '5px' }}
                  onClick={onCloseClick}
                  title={t('Toggle full screen')}
                  aria-label={t('Toggle full screen')}
                >
                  <Icon icon="fas fa-compress" size="medium" />
                </IconButton>
              )}
              <MRT_ToggleGlobalFilterButton table={table} color="primary" style={{ padding: '5px' }} />
              {!hideDownloadButton && <ExportDatasetsMenu datasets={datasetsInExport} datasetNames={datasetNames} />}
            </Box>
          )}
          muiTablePaperProps={{
            elevation: 0,
          }}
          muiTableHeadCellProps={({ column }) => {
            if (column.id === 'mrt-row-expand') {
              return {
                style: {
                  width: '10px',
                  paddingRight: '10px',

                  textAlign: 'left',
                },
              }
            }
            return {}
          }}
          muiTableFooterCellProps={({ column }) => {
            return {
              align: (column.columnDef as TimeSeriesDataTableColumnDef).align,
              sx: {
                display: 'block',
                fontFamily: 'monospace, monospace',
                fontSize: '110%',
                fontWeight: '600',
                paddingRight: '8px',
                color: variablesColors.black,
              },
            }
          }}
          muiTableBodyCellProps={muiTableBodyCellProps}
          muiTableBodyRowProps={({ row }) => {
            return row.depth > 0 ? { className: styles.TimeSeriesDataTable_QuarterRow } : {}
          }}
          muiTableContainerProps={muiTableContainerProps}
        />
      </ThemeProvider>
    </StyledEngineProvider>
  )
}
